import {Carousel} from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import React, {useEffect, useState} from "react";
import {focusedVehicleSelector, focusVehicle, isMobileSelector} from "../../chatbotSlice";
import { selectOpenedVehicleCardVin, VehicleDetails } from "../../vehicleDetailsSlice";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {VehicleCard} from "./VehicleSlide/VehicleCard";
import {VehicleSliderButton, SliderButtonPosition} from "./VehicleSliderButton";
import { useVehicleDetails } from "./useVehicleDetails";
import { VehicleDetailsView } from "./VehicleDetailsCard/VehicleDetailsView";

interface VehicleSliderProps {
    vehicles: VehicleDetails[]
}

export const RENDER_DISTANCE = 2

export const VehicleSlider: React.FC<VehicleSliderProps> = ({vehicles}) => {

    const dispatch = useAppDispatch();
    const focusedVehicle = useAppSelector(focusedVehicleSelector);
    const isMobile = useAppSelector(isMobileSelector);
    const [derivativeHeight, setDerivativeHeight] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);
    const { 
        isOpen, 
        selectedVehicle, 
        openVehicleDetails,
        handleOpenVehicleDetailsClick,
        closeVehicleDetails, 
        setModalRef,
        isLoading,
        error,
        fullVehicleDetails
    } = useVehicleDetails();
    const currentVehicleCardVin = useAppSelector(selectOpenedVehicleCardVin);

    useEffect(() => {
        if (currentVehicleCardVin && vehicles.length > 0) {
            const index = vehicles.findIndex(v => v.vin === currentVehicleCardVin);
            if (index !== -1) {
                openVehicleDetails(vehicles[index]);
            }
        }
    }, [currentVehicleCardVin]);

    function getInitialSlideIndex() {
        if (focusedVehicle && vehicles.findIndex(v => v.vin == focusedVehicle) != -1) {
            return vehicles.findIndex(v => v.vin == focusedVehicle)
        }
        return 0;
    }

    const initialSlideIndex = getInitialSlideIndex();

    const [currentVehicleIndex, setCurrentVehicleIndex] = useState(initialSlideIndex);

    useEffect(() => {
        setFocusVehicle(vehicles[currentVehicleIndex].vin)
    }, [vehicles, currentVehicleIndex])

    const setFocusVehicle = (value: string) => {
        dispatch(focusVehicle({VIN: value}))
    };

    const reportContentHeight = (nextHeaderHeight: number, nextDerivativeHeight: number) => {
        if (nextHeaderHeight > headerHeight) {
            setHeaderHeight(prev => {
                return Math.max(prev, nextHeaderHeight);
            });
        }
        if (nextDerivativeHeight > derivativeHeight) {
            setDerivativeHeight(prev => {
                return Math.max(prev, nextDerivativeHeight);
            });
        }
    }

    const isSingleVehicle = vehicles.length === 1;

    return (
        <>
            <div className="w-[calc(100%+2rem)] relative -mx-4 pl-2">
                {!isMobile && (
                    <>
                        <div className="absolute top-1/2 left-2 transform-gpu -translate-y-1/2 z-50">
                            <VehicleSliderButton
                                currentVehicleIndex={currentVehicleIndex}
                                setCurrentVehicleIndex={setCurrentVehicleIndex}
                                vehicles={vehicles}
                                position={SliderButtonPosition.BUTTON_LEFT}
                            />
                        </div>
                        <div className="absolute top-1/2 right-2 transform-gpu -translate-y-1/2 z-50">
                            <VehicleSliderButton
                                currentVehicleIndex={currentVehicleIndex}
                                setCurrentVehicleIndex={setCurrentVehicleIndex}
                                vehicles={vehicles}
                                position={SliderButtonPosition.BUTTON_RIGHT}
                            />
                        </div>
                    </>
                )}
                <div className={`${isSingleVehicle ? 'w-[85%]' : 'w-full'}`}>
                    <Carousel
                        showThumbs={false}
                        dynamicHeight={false}
                        selectedItem={currentVehicleIndex}
                        showIndicators={false}
                        emulateTouch={true}
                        showStatus={false}
                        showArrows={false}
                        preventMovementUntilSwipeScrollTolerance={true}
                        swipeScrollTolerance={15}
                        centerMode={true}
                        centerSlidePercentage={80}
                        onChange={(i) => {
                            if (!!vehicles.length) {
                                setFocusVehicle(vehicles[i].vin);
                            }
                            setCurrentVehicleIndex(i);
                        }}
                    >
                        {vehicles.map((item, i) => {
                            const distance = Math.abs(currentVehicleIndex - i);
                            const shouldRender = distance <= RENDER_DISTANCE;
                            return shouldRender ? 
                                <VehicleCard 
                                    vehicle={item}
                                    key={item.vin}
                                    reportContentHeight={reportContentHeight}
                                    derivativeHeight={derivativeHeight}
                                    headerHeight={headerHeight}
                                    onViewDetails={() => handleOpenVehicleDetailsClick(item)}
                                />
                                : <div className="w-full h-full bg-gray-100"/>
                        })}
                    </Carousel>
                </div>
            </div>
            
            {isOpen && selectedVehicle && (
                <VehicleDetailsView 
                    vehicle={selectedVehicle} 
                    onClose={closeVehicleDetails}
                    setModalRef={setModalRef}
                    isOpen={isOpen}
                    isLoading={isLoading}
                    error={error}
                    fullVehicleDetails={fullVehicleDetails}
                />
            )}
        </>
    );
}
